import React, { useMemo } from "react";
import { consultaDeFeriado } from "./feriados";
import {
  buildYearMonths,
  formatMonthShort,
  getWeekdayLabels,
  groupEventsByDay,
  isSameDay,
  startOfDayLocal,
} from "./calendarGrid";
import {
  MiniDay,
  MiniDaysGrid,
  MiniMonth,
  MiniMonthTitle,
  MiniWeekdayCell,
  MiniWeekdayRow,
  YearGrid,
} from "./styles";

const TONE_PRIORITY = ["danger", "today", "warning", "info", "success", "default"];

function pickTone(events) {
  if (!events || events.length === 0) return null;
  let best = null;
  let bestRank = TONE_PRIORITY.length;
  events.forEach((event) => {
    const rank = TONE_PRIORITY.indexOf(event.tone);
    if (rank !== -1 && rank < bestRank) {
      bestRank = rank;
      best = event.tone;
    }
  });
  return best ?? "default";
}

export default function YearView({
  feriadoDe = consultaDeFeriado(),
  cursor,
  events,
  weekStartsOn,
  vocab = { item: ["entrega", "entregas"], previsao: ["previsão sem lote", "previsões sem lote"] },
  onJumpToMonth,
  onJumpToDay,
}) {
  const year = cursor.getFullYear();
  const today = useMemo(() => startOfDayLocal(new Date()), []);
  const months = useMemo(() => buildYearMonths(year, weekStartsOn), [year, weekStartsOn]);
  const weekdays = useMemo(
    () => getWeekdayLabels(weekStartsOn).map((label) => label.charAt(0)),
    [weekStartsOn]
  );
  const eventsByDay = useMemo(() => groupEventsByDay(events), [events]);

  return (
    <YearGrid>
      {months.map(({ month, cells }) => {
        const isCurrentMonth =
          today.getFullYear() === year && today.getMonth() === month;
        return (
          <MiniMonth key={month}>
            <MiniMonthTitle
              type="button"
              $current={isCurrentMonth}
              onClick={() => onJumpToMonth?.(new Date(year, month, 1))}
              title="Abrir mês"
            >
              {formatMonthShort(year, month)}
            </MiniMonthTitle>
            <MiniWeekdayRow>
              {weekdays.map((char, idx) => (
                <MiniWeekdayCell key={`${month}-w-${idx}`}>{char}</MiniWeekdayCell>
              ))}
            </MiniWeekdayRow>
            <MiniDaysGrid>
              {cells.map((cell) => {
                const isOutside = !cell.inMonth;
                const isToday = isSameDay(cell.date, today);
                // Dia do mes VIZINHO (apagado, so fecha a semana) nao carrega
                // nada: nem cor, nem clique, nem rotulo. O rotulo escapava —
                // o "29" apagado dentro de outubro anunciava, no balao e para
                // o leitor de tela, a entrega que e de setembro, e o mesmo
                // dia aparecia contado em dois meses.
                const list = isOutside ? [] : eventsByDay.get(cell.key) || [];
                const hasEvents = list.length > 0;
                // Previsoes (previsao: true) so pintam o dia quando nao ha
                // nada programado nele — e ai o dia sai tracejado, como os
                // chips das outras escalas, para nao parecer entrega.
                const programadas = list.filter((event) => !event.previsao);
                const previsoes = list.length - programadas.length;
                const soPrevisao = hasEvents && programadas.length === 0;
                const tone = hasEvents ? pickTone(soPrevisao ? list : programadas) : null;
                const nomeFeriado =
                  !isOutside ? feriadoDe(cell.date) : null;
                const rotulo = [
                  nomeFeriado ? `Feriado: ${nomeFeriado}` : null,
                  programadas.length > 0
                    ? `${programadas.length} ${vocab.item[programadas.length === 1 ? 0 : 1]}`
                    : null,
                  previsoes > 0
                    ? `${previsoes} ${vocab.previsao[previsoes === 1 ? 0 : 1]}`
                    : null,
                ]
                  .filter(Boolean)
                  .join(" · ");
                const clickable = !isOutside && (hasEvents || true);

                return (
                  <MiniDay
                    key={cell.key}
                    type="button"
                    $outside={isOutside}
                    $today={isToday}
                    $hasEvents={hasEvents}
                    $previsao={soPrevisao}
                    $tone={tone}
                    $clickable={clickable}
                    $feriado={Boolean(nomeFeriado)}
                    onClick={() => {
                      if (isOutside) return;
                      onJumpToDay?.(cell.date);
                    }}
                    aria-label={
                      rotulo ? `${cell.date.getDate()} — ${rotulo}` : String(cell.date.getDate())
                    }
                    title={rotulo || undefined}
                  >
                    {cell.date.getDate()}
                  </MiniDay>
                );
              })}
            </MiniDaysGrid>
          </MiniMonth>
        );
      })}
    </YearGrid>
  );
}
